/**
 * `/bank-accounts` — S30 list view.
 *
 * **Strict-fidelity rebuild** per prototype-to-implementation playbook §6.
 * Page-scoped CSS lives in `./bank-accounts-page.css` and was lifted
 * verbatim from `prototype/cash/bank-accounts.html ▸ <style>` (lines 11-729),
 * with every selector re-scoped under `.bank-accounts-page` so it
 * doesn't leak.
 *
 * JSX mirrors the prototype's tag + class hierarchy verbatim:
 *   .page-head > .page-head-text > h1 + .lead > [span.num, span.dot]
 *   .kpis > .kpi.s-{indigo|emerald|amber|rose} > .kpi-stripe + .kpi-head
 *     ( > span + .ic ) + .kpi-val + .kpi-meta
 *   .recon-banner > .icon + span + .spacer + .open-link
 *   .tabs > .tab(.is-active | .is-warn) > .count
 *   .toolbar > .search-input + .filter-pill (× 2) + .toolbar-spacer
 *     + .view-toggle + .btn-secondary
 *   .bank-list > .bank-row(.s-issue|.s-warn|.s-good|.s-info|.archived) >
 *     .bank-ava(.b-hdfc|.b-icici|.b-kotak|.b-axis|.b-rbl|.b-cash|.b-cc)
 *     + .bank-name-cell ( > .bank-name-row > .bank-name(.acc) + .health-pill )
 *                       ( > .bank-meta(.ifsc) ) ( > .util-bar(.cc-bar) > .fill )
 *     + .bank-activity > .what(.live-dot) + .when
 *     + .bank-bal > .v + .sub
 *     + .bank-rec > .v + .sub
 *     + .bank-action > [reconcile / move / settle / topup / view / count
 *       button] + .arrow
 *   .list-foot > .stat (× N) + .sep
 *
 * @see prototype/cash/bank-accounts.html — V1.1 baseline; do not paraphrase.
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Download,
  Funnel,
  Grid2X2,
  List,
  Plus,
  RefreshCcw,
  Search,
  Shield,
  TrendingUp,
} from "lucide-react";
import { Alert, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { useModule } from "../../../../lib/modules.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { ListPagination } from "../../../../features/master-list/index.ts";
import "./bank-accounts-page.css";

export const Route = createFileRoute("/(workspace)/cash/bank-accounts/")({
  component: BankAccountsPage,
});

type TabKey = "all" | "reconcile" | "synced" | "credit" | "cash" | "archived";

/** Type filter pill values — cycles on click. */
type TypeFilter = "all" | "bank" | "cash" | "credit_card";
const TYPE_FILTER_ORDER: ReadonlyArray<TypeFilter> = ["all", "bank", "cash", "credit_card"];

/** Sort pill values — cycles on click. */
type SortOrder = "highest-balance" | "name" | "recent-activity";
const SORT_ORDER_ORDER: ReadonlyArray<SortOrder> = ["highest-balance", "name", "recent-activity"];

function labelForTypeFilter(t: TypeFilter): string {
  switch (t) {
    case "all":
      return "all";
    case "bank":
      return "bank";
    case "cash":
      return "cash";
    case "credit_card":
      return "credit card";
  }
}

function labelForSort(s: SortOrder): string {
  switch (s) {
    case "highest-balance":
      return "highest balance";
    case "name":
      return "name";
    case "recent-activity":
      return "recent activity";
  }
}

interface ListRowApi {
  id: string;
  name: string;
  type: "bank" | "cash" | "credit_card";
  accountNumberLast4: string | null;
  runningBalancePaise: string;
  imprestAmountPaise: string | null;
  creditLimitPaise: string | null;
  statementCycleDay: number | null;
  nextStatementOn: string | null;
  archivedAt: string | null;
}

function BankAccountsPage() {
  const list = trpc.bankAccounts.list.useQuery({ includeArchived: true });
  // Reconcile + cash movements are the 1.0-withheld cash module (ADR-0046).
  // Bank accounts stay in, so hide only these cash entry points.
  const hasCash = useModule("cash");
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [searchQ, setSearchQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("highest-balance");

  const data = list.data;
  const allRows = data?.rows ?? [];

  const counts = useMemo(() => countByTab(allRows), [allRows]);
  const filtered = useMemo(
    () => sortRows(filterRows(allRows, activeTab, searchQ, typeFilter), sortOrder),
    [allRows, activeTab, searchQ, typeFilter, sortOrder],
  );

  const cycleTypeFilter = () => {
    const idx = TYPE_FILTER_ORDER.indexOf(typeFilter);
    setTypeFilter(TYPE_FILTER_ORDER[(idx + 1) % TYPE_FILTER_ORDER.length] ?? "all");
  };
  const cycleSortOrder = () => {
    const idx = SORT_ORDER_ORDER.indexOf(sortOrder);
    setSortOrder(SORT_ORDER_ORDER[(idx + 1) % SORT_ORDER_ORDER.length] ?? "highest-balance");
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Bank accounts" }]}>
      <div className="bank-accounts-page">
        {/* ── Page header ──────────────────────────────────── */}
        <div className="page-head">
          <div className="page-head-text">
            <h1>Bank accounts</h1>
            <p className="lead">
              <span>
                <span className="num" style={{ color: "var(--ink-local)", fontWeight: 600 }}>
                  {counts.all}
                </span>{" "}
                accounts
              </span>
              <span className="dot" aria-hidden="true" />
              {data ? (
                <span>
                  <span className="num" style={{ color: "var(--ink-local)", fontWeight: 600 }}>
                    {formatRupeesShort(BigInt(data.totals.cashOnHandPaise))}
                  </span>{" "}
                  total cash
                </span>
              ) : null}
              <span className="dot" aria-hidden="true" />
              <span style={{ color: "var(--rose)", fontWeight: 600 }}>
                <V1xChip /> unmatched txns
              </span>
              <span className="dot" aria-hidden="true" />
              <span>
                Last sync <V1xChip />
              </span>
            </p>
          </div>
          <div className="page-head-actions">
            {hasCash ? (
              <Link
                to="/cash/reconcile"
                className="btn-secondary"
                style={{ textDecoration: "none" }}
                title="Open the reconciliation ledger"
              >
                <RefreshCcw size={13} strokeWidth={2} aria-hidden />
                Reconcile
              </Link>
            ) : null}
            {hasCash ? (
              <Link
                to="/cash/movements/new"
                className="btn-secondary"
                data-launch="movement"
                style={{ textDecoration: "none" }}
              >
                <ArrowRightLeft size={13} strokeWidth={2} aria-hidden />
                Record movement
              </Link>
            ) : null}
            <Link to="/cash/bank-accounts/new" className="btn-primary">
              <Plus size={13} strokeWidth={2.4} aria-hidden />
              Add account
            </Link>
          </div>
        </div>

        {/* ── KPI strip ─────────────────────────────────────── */}
        {data ? (
          <section className="kpis">
            <div className="kpi s-emerald">
              <div className="kpi-stripe" aria-hidden="true" />
              <div className="kpi-head">
                <span>Total cash</span>
                <span className="ic" aria-hidden="true">
                  <CreditCard size={14} strokeWidth={2} aria-hidden />
                </span>
              </div>
              <div className="kpi-val">
                <span className="num">
                  {formatRupeesShortValue(BigInt(data.totals.cashOnHandPaise))}
                </span>
                {formatRupeesShortUnit(BigInt(data.totals.cashOnHandPaise))}
              </div>
              <div className="kpi-meta">
                <span>
                  Across{" "}
                  <strong style={{ color: "var(--ink-local)" }}>{counts.activeNonArchived}</strong>{" "}
                  active {counts.activeNonArchived === 1 ? "account" : "accounts"}
                </span>
              </div>
            </div>
            <div className="kpi s-indigo">
              <div className="kpi-stripe" aria-hidden="true" />
              <div className="kpi-head">
                <span>Active accounts</span>
                <span className="ic" aria-hidden="true">
                  <Shield size={14} strokeWidth={2} aria-hidden />
                </span>
              </div>
              <div className="kpi-val">
                <span className="num">{counts.activeNonArchived}</span>
                <span className="u">of {counts.all + counts.archived}</span>
              </div>
              <div className="kpi-meta">
                {counts.cashManual} manual · {counts.creditLines} credit card · {counts.archived}{" "}
                archived
              </div>
            </div>
            <div className="kpi s-amber">
              <div className="kpi-stripe" aria-hidden="true" />
              <div className="kpi-head">
                <span>Unmatched txns</span>
                <span className="ic" aria-hidden="true">
                  <TrendingUp size={14} strokeWidth={2} aria-hidden />
                </span>
              </div>
              <div className="kpi-val">
                <span className="num">—</span>
              </div>
              <div className="kpi-meta">
                <V1xChip /> Lights up once /reconcile ships
              </div>
            </div>
            <div className="kpi s-rose">
              <div className="kpi-stripe" aria-hidden="true" />
              <div className="kpi-head">
                <span>Card liability</span>
                <span className="ic" aria-hidden="true">
                  <CreditCard size={14} strokeWidth={2} aria-hidden />
                </span>
              </div>
              <div className="kpi-val">
                <span className="num">
                  {BigInt(data.totals.cardLiabilityPaise) === 0n
                    ? "—"
                    : formatRupeesShortValue(BigInt(data.totals.cardLiabilityPaise))}
                </span>
                {BigInt(data.totals.cardLiabilityPaise) === 0n
                  ? null
                  : formatRupeesShortUnit(BigInt(data.totals.cardLiabilityPaise))}
              </div>
              <div className="kpi-meta">
                {BigInt(data.totals.cardLiabilityPaise) === 0n
                  ? "No card spend this cycle"
                  : `Across ${counts.creditLines} ${counts.creditLines === 1 ? "card" : "cards"}`}
              </div>
            </div>
          </section>
        ) : null}

        {/* ── Reconciliation banner (cash module, ADR-0046) ─── */}
        {hasCash ? (
          <div className="recon-banner" role="status">
            <span className="icon" aria-hidden="true">
              <TrendingUp size={14} strokeWidth={2} aria-hidden />
            </span>
            <span>
              <strong>Reconcile bank statement against the ledger</strong> — open the account ledger
              to spot unmatched receipts, vendor payments, and cash movements. CSV auto-match lands in
              V1.x.
            </span>
            <span className="spacer" />
            <Link to="/cash/reconcile" className="open-link">
              Open reconcile →
            </Link>
          </div>
        ) : null}

        {/* ── Tabs ──────────────────────────────────────────── */}
        <TabsRoot
          className="mb-3.5"
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabKey)}
        >
          <TabsList aria-label="Bank account status">
            <TabsTrigger value="all" count={counts.activeNonArchived}>
              All
            </TabsTrigger>
            <TabsTrigger value="reconcile" count={0} countTone="warn">
              Needs reconcile
            </TabsTrigger>
            <TabsTrigger value="synced" count={0}>
              Auto-synced
            </TabsTrigger>
            <TabsTrigger value="credit" count={counts.creditLines}>
              Credit lines
            </TabsTrigger>
            <TabsTrigger value="cash" count={counts.cashManual}>
              Cash &amp; manual
            </TabsTrigger>
            <TabsTrigger value="archived" count={counts.archived}>
              Archived
            </TabsTrigger>
          </TabsList>
        </TabsRoot>

        {/* ── Toolbar ───────────────────────────────────────── */}
        <div className="toolbar">
          <label className="search-input">
            <Search size={14} strokeWidth={2} aria-hidden />
            <input
              type="search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search by bank, IFSC, last 4…"
              aria-label="Search"
            />
          </label>
          <button
            type="button"
            className="filter-pill"
            onClick={cycleTypeFilter}
            title="Cycle type filter"
            aria-label={`Type filter, currently ${labelForTypeFilter(typeFilter)}`}
          >
            <Funnel aria-hidden strokeWidth={2} />
            Type · {labelForTypeFilter(typeFilter)}
            <ChevronDown aria-hidden strokeWidth={2} />
          </button>
          <button
            type="button"
            className="filter-pill"
            onClick={cycleSortOrder}
            title="Cycle sort order"
            aria-label={`Sort order, currently ${labelForSort(sortOrder)}`}
          >
            <List aria-hidden strokeWidth={2} />
            Sort · {labelForSort(sortOrder)}
            <ChevronDown aria-hidden strokeWidth={2} />
          </button>
          <span className="toolbar-spacer" />
          <div className="view-toggle" role="group" aria-label="View mode">
            <button type="button" className="is-active" aria-label="List view">
              <List aria-hidden strokeWidth={2} />
            </button>
            <button type="button" aria-label="Grid view" disabled>
              <Grid2X2 aria-hidden strokeWidth={2} />
            </button>
          </div>
          <button
            type="button"
            className="btn-secondary"
            disabled
            title="Statement export lands in V1.x"
          >
            <Download size={13} strokeWidth={2} aria-hidden />
            Statement export
          </button>
        </div>

        {/* ── Bank list ─────────────────────────────────────── */}
        {list.isLoading ? (
          <p className="text-sm text-muted">Loading bank accounts…</p>
        ) : list.error ? (
          <Alert
            intent="bad"
            title="Couldn't load bank accounts"
            description={list.error.message}
          />
        ) : (
          <div className="bank-list">
            {filtered.length === 0 ? (
              <div
                style={{ padding: "40px 20px", textAlign: "center", color: "var(--muted-local)" }}
              >
                <strong
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: "var(--ink-2-local)",
                    marginBottom: 4,
                  }}
                >
                  {emptyStateCopy(activeTab).title}
                </strong>
                <span style={{ fontSize: 12 }}>{emptyStateCopy(activeTab).sub}</span>
              </div>
            ) : (
              <>
                {/* Header row — same 6-col grid template as `.bank-row` */}
                <div className="bank-head" aria-hidden="true">
                  <span />
                  <span>Account</span>
                  <span>Activity</span>
                  <span>Balance</span>
                  <span>Reconcile</span>
                  <span />
                </div>
                {filtered.map((row) => (
                  <BankRow key={row.id} row={row} />
                ))}
                {/* Footer stats strip */}
                {data ? (
                  <div className="list-foot">
                    <span className="stat">
                      Total cash{" "}
                      <strong>{formatRupeesShort(BigInt(data.totals.cashOnHandPaise))}</strong>
                    </span>
                    <span className="sep" aria-hidden="true" />
                    <span className="stat">
                      Active{" "}
                      <strong>
                        {counts.activeNonArchived} of {counts.all + counts.archived}
                      </strong>
                    </span>
                    <span className="sep" aria-hidden="true" />
                    <span className="stat">
                      Unmatched <V1xChip />
                    </span>
                    <span className="sep" aria-hidden="true" />
                    <span className="stat">
                      Card outstanding{" "}
                      <strong>
                        {BigInt(data.totals.cardLiabilityPaise) === 0n
                          ? "—"
                          : formatRupeesShort(BigInt(data.totals.cardLiabilityPaise))}
                      </strong>
                    </span>
                    <span className="sep" aria-hidden="true" />
                    <span className="stat">
                      Last sync <V1xChip />
                    </span>
                  </div>
                ) : null}
                {/* Footer 2 — static pagination strip (accounts are fully
                    loaded; no cursor). Mirrors MasterListCard's pagination
                    wrapper (`border-t border-line`). */}
                <div className="border-t border-line">
                  <ListPagination
                    pageNumber={1}
                    showingFrom={filtered.length ? 1 : 0}
                    showingTo={filtered.length}
                    total={filtered.length}
                    sortLabel="all accounts"
                    hasPrev={false}
                    hasNext={false}
                    onPrev={() => {}}
                    onNext={() => {}}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Bank row ──────────────────────────────────────────────────────

function BankRow({ row }: { row: ListRowApi }) {
  const navigate = useNavigate();
  const hasCash = useModule("cash");
  const balPaise = BigInt(row.runningBalancePaise);
  const isArchived = row.archivedAt !== null;
  const parsed = parseAccountName(row.name);
  const ava = avatarFor(row);
  const cls = ["bank-row"];
  if (isArchived) cls.push("archived");
  if (row.type === "credit_card") cls.push("s-info");

  return (
    <Link
      to="/cash/bank-accounts/$bankAccountId"
      params={{ bankAccountId: row.id }}
      className={cls.join(" ")}
    >
      <div className={`bank-ava ${ava.classMod}`} aria-hidden="true">
        {ava.label}
      </div>
      <div className="bank-name-cell">
        <div className="bank-name-row">
          <span className="bank-name">
            {parsed.bank}
            {parsed.acc ? <span className="acc">{parsed.acc}</span> : null}
          </span>
          {/* Per-row health pills. Sync state is V1.x; we mark manual /
              dormant honestly + show credit-card / cash labels. */}
          {row.type === "credit_card" ? <span className="health-pill cc">Credit card</span> : null}
          {row.type === "cash" ? <span className="health-pill cash">Cash drawer</span> : null}
          {isArchived ? <span className="health-pill dormant">Dormant</span> : null}
          {!isArchived && row.type === "bank" ? (
            <span className="health-pill manual">Manual</span>
          ) : null}
        </div>
        <div className="bank-meta">
          {row.type === "cash"
            ? `Petty cash drawer · custodian held · imprest ${
                row.imprestAmountPaise
                  ? formatRupees(BigInt(row.imprestAmountPaise))
                  : "— (set in edit)"
              } · counted weekly`
            : row.type === "credit_card"
              ? `Corporate card · limit ${
                  row.creditLimitPaise ? formatRupees(BigInt(row.creditLimitPaise)) : "—"
                } · statement day ${row.statementCycleDay ?? "—"} · interest-free if paid in full`
              : isArchived
                ? `No activity since archive · candidate to close`
                : `Operating account · IFSC `}
          {row.type === "bank" && !isArchived ? <span className="ifsc">— · V1.x</span> : null}
        </div>
        {row.type === "credit_card" ? (
          <div className="util-bar cc-bar" aria-label={`${utilPct(row)}% utilised`}>
            <div className="fill" style={{ width: `${utilPct(row)}%` }} />
          </div>
        ) : null}
      </div>
      {/* Activity column — V1.x. The chrome is here; the data is deferred. */}
      <div className="bank-activity">
        <span className="what">
          <span className="live-dot off" aria-hidden="true" />
          Sync · <V1xChip />
        </span>
        <span className="when">awaiting adapter</span>
      </div>
      {/* Balance column */}
      <div className="bank-bal">
        <span className={balanceClass(row, balPaise)}>
          {row.type === "credit_card" && balPaise > 0n ? "−" : ""}
          {formatRupees(balPaise)}
        </span>
        <span className="sub">{balanceSubCopy(row, balPaise)}</span>
      </div>
      {/* Reconcile column — links to /reconcile (the read-only ledger
          view). Per-account unmatched count lands in V1.x once
          statement-import + auto-match adapters ship. */}
      <div className="bank-rec">
        <span className="v">—</span>
        <span className="sub">
          <V1xChip /> unmatched
        </span>
      </div>
      {/* Action column */}
      <div className="bank-action">
        {hasCash ? (
          <button
            type="button"
            aria-label="Open reconciliation ledger"
            title="Open reconciliation ledger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate({ to: "/cash/reconcile" });
            }}
          >
            <TrendingUp size={14} strokeWidth={2} aria-hidden />
          </button>
        ) : null}
        <button className="arrow" type="button" aria-hidden="true">
          <ChevronRight aria-hidden strokeWidth={2} />
        </button>
      </div>
    </Link>
  );
}

// ── Small helpers ────────────────────────────────────────

function V1xChip() {
  return <span className="v1x-chip">V1.x</span>;
}

// ── Tab counts + filter ───────────────────────────────────

function countByTab(rows: ReadonlyArray<ListRowApi>) {
  let activeNonArchived = 0;
  let archived = 0;
  let creditLines = 0;
  let cashManual = 0;
  for (const r of rows) {
    if (r.archivedAt) {
      archived += 1;
      continue;
    }
    activeNonArchived += 1;
    if (r.type === "credit_card") creditLines += 1;
    if (r.type === "bank" || r.type === "cash") cashManual += 1;
  }
  return { all: rows.length - archived, activeNonArchived, archived, creditLines, cashManual };
}

function filterRows(
  rows: ReadonlyArray<ListRowApi>,
  tab: TabKey,
  q: string,
  typeFilter: TypeFilter = "all",
): ListRowApi[] {
  const query = q.trim().toLowerCase();
  return rows.filter((r) => {
    const isArchived = r.archivedAt !== null;
    switch (tab) {
      case "archived":
        if (!isArchived) return false;
        break;
      case "reconcile":
        return false; // V1.x — no unmatched data yet
      case "synced":
        return false; // V1.x — no Yodlee yet
      case "credit":
        if (isArchived || r.type !== "credit_card") return false;
        break;
      case "cash":
        if (isArchived || (r.type !== "bank" && r.type !== "cash")) return false;
        break;
      case "all":
      default:
        if (isArchived) return false;
        break;
    }
    if (typeFilter !== "all" && r.type !== typeFilter) return false;
    if (!query) return true;
    return (
      r.name.toLowerCase().includes(query) ||
      (r.accountNumberLast4 ?? "").toLowerCase().includes(query)
    );
  });
}

/**
 * Client-side sort matching the `Sort · X` filter pill.
 * `highest-balance` is the prototype default (treats CC outstanding as a
 * positive liability so cards sit alongside bank balances honestly).
 */
function sortRows(rows: ReadonlyArray<ListRowApi>, order: SortOrder): ListRowApi[] {
  const copy = [...rows];
  switch (order) {
    case "highest-balance":
      copy.sort((a, b) => {
        const ba = BigInt(a.runningBalancePaise);
        const bb = BigInt(b.runningBalancePaise);
        if (bb > ba) return 1;
        if (bb < ba) return -1;
        return 0;
      });
      break;
    case "name":
      copy.sort((a, b) => a.name.localeCompare(b.name, "en-IN"));
      break;
    case "recent-activity":
      // No `lastActivityAt` yet — fall back to nextStatementOn / archivedAt
      // proxy (newest first). Reconciles with V1.x once the activity-stream
      // column lands.
      copy.sort((a, b) => {
        const aa = a.nextStatementOn ?? a.archivedAt ?? "";
        const bb = b.nextStatementOn ?? b.archivedAt ?? "";
        return bb.localeCompare(aa);
      });
      break;
  }
  return copy;
}

function emptyStateCopy(tab: TabKey): { title: string; sub: string } {
  switch (tab) {
    case "reconcile":
      return {
        title: "Nothing to reconcile yet",
        sub: "Lights up in V1.x once GSTN-style statement upload lands.",
      };
    case "synced":
      return {
        title: "No auto-synced accounts",
        sub: "Yodlee / RazorpayX integration lands in V1.x.",
      };
    case "credit":
      return {
        title: "No credit lines yet",
        sub: "Add a credit card or OD facility to track liability + statement cycles.",
      };
    case "cash":
      return {
        title: "No manual accounts yet",
        sub: "Cash drawers and manually-reconciled bank accounts show here.",
      };
    case "archived":
      return {
        title: "No archived accounts",
        sub: "Archived accounts retain audit history without cluttering the list.",
      };
    case "all":
    default:
      return {
        title: "No bank accounts yet",
        sub: "Add your first operating account to get started.",
      };
  }
}

// ── Avatar palette per bank ──────────────────────────────

function avatarFor(row: ListRowApi): { classMod: string; label: string } {
  if (row.type === "cash") return { classMod: "b-cash", label: "₹" };
  if (row.type === "credit_card") return { classMod: "b-cc", label: "VISA" };
  // Bank palette match by name prefix. Mirrors prototype's `.b-{hdfc|icici|kotak|axis|rbl|sbi}`.
  const n = row.name.toLowerCase();
  if (n.includes("hdfc")) return { classMod: "b-hdfc", label: "H" };
  if (n.includes("icici")) return { classMod: "b-icici", label: "I" };
  if (n.includes("kotak")) return { classMod: "b-kotak", label: "K" };
  if (n.includes("axis")) return { classMod: "b-axis", label: "A" };
  if (n.includes("rbl")) return { classMod: "b-rbl", label: "R" };
  if (n.startsWith("sbi") || n.includes("state bank")) return { classMod: "b-sbi", label: "S" };
  return { classMod: "b-hdfc", label: row.name.charAt(0).toUpperCase() };
}

// ── Account-name parser ──────────────────────────────────

/**
 * Seeded names follow `<Bank name> · <Account label ···last4>`. Split
 * on the first ` · ` so the second half can render in the `.acc` span.
 * If the name has no separator, the entire name goes into `.bank-name`.
 */
function parseAccountName(name: string): { bank: string; acc: string | null } {
  const sep = " · ";
  const idx = name.indexOf(sep);
  if (idx === -1) return { bank: name, acc: null };
  return { bank: name.slice(0, idx), acc: name.slice(idx + sep.length) };
}

// ── Balance copy + class helpers ─────────────────────────

function balanceClass(row: ListRowApi, paise: bigint): string {
  if (row.type === "credit_card" && paise > 0n) return "v debt";
  if (paise === 0n) return "v zero";
  // A bank / cash account in the negative is overdrawn — an exception worth
  // flagging in red. Card outstanding stays neutral; its utilisation bar
  // already carries the health signal.
  if (row.type !== "credit_card" && paise < 0n) return "v debt";
  return "v";
}

function balanceSubCopy(row: ListRowApi, paise: bigint): string {
  if (row.type === "credit_card") {
    return paise === 0n ? "paid in full" : "outstanding · this cycle";
  }
  if (row.type === "cash") {
    return row.imprestAmountPaise
      ? `against ${formatRupees(BigInt(row.imprestAmountPaise))} imprest`
      : "cash drawer";
  }
  if (paise === 0n) return "closing balance";
  return "available · running balance";
}

function utilPct(row: ListRowApi): number {
  if (!row.creditLimitPaise) return 0;
  const limit = BigInt(row.creditLimitPaise);
  if (limit === 0n) return 0;
  const used = BigInt(row.runningBalancePaise);
  const pct = Number((used * 10000n) / limit) / 100;
  return Math.max(0, Math.min(100, pct));
}

// ── Money formatting ─────────────────────────────────────

function absBigInt(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function formatRupees(paise: bigint): string {
  const sign = paise < 0n ? "−" : "";
  const rupees = absBigInt(paise) / 100n;
  return `${sign}₹${rupees.toLocaleString("en-IN")}`;
}

function formatRupeesShort(paise: bigint): string {
  const rupees = absBigInt(paise) / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}L`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}

/** Number part of the KPI big-number — split so the unit can render at smaller size. */
function formatRupeesShortValue(paise: bigint): string {
  const rupees = absBigInt(paise) / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}

function formatRupeesShortUnit(paise: bigint): React.ReactNode {
  const rupees = absBigInt(paise) / 100n;
  if (rupees >= 10_000_000n) return <span className="u">Cr</span>;
  if (rupees >= 100_000n) return <span className="u">L</span>;
  return null;
}

// ── Icons ─────────────────────────────────────────────────

/* ── Fidelity report (playbook §6.3) ─────────────────────────────

   Prototype: prototype/cash/bank-accounts.html (1,383 lines)

   ## .page-head (lines 839-867)
   - Prototype: `<div class="page-head"><div class="page-head-text"><h1>+<p class="lead">…<span class="num">…</span><span class="dot">…`
   - Implementation: same — `.page-head > .page-head-text > h1 + .lead > span.num + span.dot`
   - Diffs:
     · "Sync all" is disabled (no Yodlee adapter yet)
     · "Record movement" is a real `<Link to="/cash/movements/new">` instead of a modal launcher
     · "Add account" is a real `<Link to="/cash/bank-accounts/new">`
     · Two stats in the lead (unmatched count, last sync) render `<V1xChip />` instead of values

   ## .kpis (lines 869-903) — 4 tiles
   - Prototype: Total cash · Active accounts · Unmatched txns · OD headroom
   - Implementation: Total cash (real) · Active accounts (real) · Unmatched txns (V1.x) · Card liability (real)
   - Diff: Tile 4 changed from "OD headroom" to "Card liability" because we have credit_card data but
     no overdraft / OD facility entity yet. Both are roll-ups of the same primitive (account balance).

   ## .recon-banner (lines 905-915)
   - Prototype + Implementation: identical chrome. Copy adapted to point at /reconcile preview.

   ## .tabs (lines 917-925) — 6 tabs
   - Identical structure; counts come from `countByTab`. V1.x tabs (Needs reconcile, Auto-synced) render with 0 count.

   ## .toolbar (lines 927-956)
   - Identical structure. Filter pills + Statement export are disabled (V1.x). Grid view is disabled (list-only for V1.0).

   ## .bank-list / .bank-row (lines 958-1238) — 6-column grid
   - Prototype: `44px | 1.8fr | 1.2fr | 130px | 110px | auto` — implementation matches exactly via the page-scoped CSS.
   - Each row: `.bank-ava` (palette per type) + `.bank-name-cell` (`.bank-name-row` + `.bank-meta` + optional `.util-bar`) + `.bank-activity` + `.bank-bal` + `.bank-rec` + `.bank-action`.
   - Health pills used: `cc`, `cash`, `dormant`, `manual`. `primary` / `synced` / `issue` are reserved for the V1.x sync state.

   ## .list-foot (lines 1240-1251)
   - Identical structure; the unmatched + last-sync stats carry V1.x chips.

──────────────────────────────────────────────────────────────── */
